/**
 * Roster vs Demand — Stage 8.
 *
 * Sources `/api/bi/roster/coverage/` to surface a per-store hour-of-day
 * heatmap of orders (demand) against distinct riders that were online
 * (coverage). The "pressure" cell colour comes from demand ÷ riders.
 */
import { useMemo, useState } from 'react';
import { Sparkles, Bike, AlertTriangle, Store } from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { KPICard } from '@/components/charts/KPICard';
import { DataView } from '@/components/data/DataView';
import {
  useRosterCoverage,
  type RosterCoverageCell,
  type RosterCoverageStoreRow,
} from '@/api/bi';
import { rangeISO, useFilters } from '@/state/filters';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

const HOURS = Array.from({ length: 24 }, (_, i) => i);

export function RosterPage() {
  const range = useFilters((s) => s.range);
  const storeIds = useFilters((s) => s.storeIds);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const { start: startISO, end: endISO } = useMemo(
    () => rangeISO(range, timeRangeMode),
    [range, timeRangeMode],
  );
  const [search, setSearch] = useState('');

  const query = useRosterCoverage({ start: startISO, end: endISO, storeIds });

  const stores = query.data?.stores ?? [];
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return stores;
    return stores.filter((r) => r.store_name.toLowerCase().includes(s));
  }, [stores, search]);

  // Restrict the heatmap to the top N most pressured stores so the page
  // stays scannable even with 30+ stores.
  const heatStores = useMemo(() => {
    const list = [...filtered].sort((a, b) => b.pressure - a.pressure);
    return list.slice(0, 12);
  }, [filtered]);

  const grid = useMemo(() => {
    const map = new Map<string, RosterCoverageCell>();
    let peak = 0;
    for (const c of query.data?.cells ?? []) {
      map.set(`${c.store_id}-${c.hour}`, c);
      if (c.pressure > peak) peak = c.pressure;
    }
    return { map, peak };
  }, [query.data?.cells]);

  const totals = query.data?.totals;
  const worst = totals?.worst_cell;

  const columns = useMemo<ColumnDef<RosterCoverageStoreRow, unknown>[]>(
    () => [
      {
        id: 'store',
        header: 'Store',
        accessorKey: 'store_name',
        cell: ({ getValue }) => <span className="font-medium text-foreground">{getValue<string>()}</span>,
      },
      {
        id: 'demand',
        header: 'Demand',
        accessorKey: 'demand',
        cell: ({ getValue }) => <span className="kpi-num">{fmt.number(getValue<number>())}</span>,
      },
      {
        id: 'rider_hours',
        header: 'Rider-hours',
        accessorKey: 'rider_hours',
        cell: ({ getValue }) => <span className="kpi-num">{fmt.number(getValue<number>())}</span>,
      },
      {
        id: 'pressure',
        header: 'Pressure',
        accessorKey: 'pressure',
        cell: ({ getValue }) => {
          const v = getValue<number>();
          const tone =
            v >= 5
              ? 'danger'
              : v >= 2.5
                ? 'warning'
                : v >= 1
                  ? 'info'
                  : 'success';
          return <Badge tone={tone}>{v.toFixed(2)} / hr·rider</Badge>;
        },
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        eyebrow="Workforce"
        title="Roster vs demand"
        description="Where the orders pile up before the riders do. Hour×store heatmap surfaces the gap honestly."
        actions={
          <Badge tone="info" className="gap-1">
            <Sparkles className="h-3 w-3" /> Stage 8
          </Badge>
        }
      />

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard label="Stores" value={fmt.number(totals?.stores ?? 0)} loading={query.isLoading} />
        <KPICard label="Demand" value={fmt.number(totals?.demand ?? 0)} loading={query.isLoading} />
        <KPICard
          label="Rider-hours"
          value={fmt.number(totals?.rider_hours ?? 0)}
          loading={query.isLoading}
        />
        <KPICard
          label="Avg pressure"
          value={`${(totals?.avg_pressure ?? 0).toFixed(2)} / hr·rider`}
          goodWhenUp={false}
          loading={query.isLoading}
          hint="Orders ÷ rider-hours across the window."
        />
      </div>

      {worst ? (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-xs">
          <AlertTriangle className="h-4 w-4 text-warning" />
          <span className="text-foreground">
            Worst hour: <span className="font-medium">{worst.store_name}</span> @ {pad(worst.hour)}:00 —
          </span>
          <span className="text-muted-foreground">
            {fmt.number(worst.demand)} orders vs {fmt.number(worst.coverage)} riders
            ({worst.pressure.toFixed(2)} / hr·rider)
          </span>
        </div>
      ) : null}

      <Card className="mt-4">
        <CardBody>
          {query.isError ? (
            <ErrorState description="Couldn't load roster coverage." onRetry={() => void query.refetch()} />
          ) : query.isLoading ? (
            <div className="h-72 animate-pulse rounded-md bg-muted/40" />
          ) : heatStores.length === 0 ? (
            <EmptyState
              icon={<Store className="h-5 w-5" />}
              title="No store coverage in this window"
              description="No shifts were recorded for the selected dates. Try a wider range or a different store."
              className="h-72"
            />
          ) : (
            <div className="overflow-x-auto">
              <div className="min-w-[840px]">
                <div className="grid grid-cols-[160px_repeat(24,minmax(0,1fr))] gap-1 text-[10px] text-muted-foreground">
                  <div />
                  {HOURS.map((h) => (
                    <div key={h} className="text-center font-mono">
                      {pad(h)}
                    </div>
                  ))}
                </div>
                {heatStores.map((store) => (
                  <div
                    key={store.store_id}
                    className="mt-1 grid grid-cols-[160px_repeat(24,minmax(0,1fr))] gap-1"
                  >
                    <div className="flex items-center truncate text-[11px] font-medium text-foreground">
                      {store.store_name}
                    </div>
                    {HOURS.map((h) => {
                      const cell = grid.map.get(`${store.store_id}-${h}`);
                      return <Cell key={h} cell={cell} peak={grid.peak} />;
                    })}
                  </div>
                ))}
                <div className="mt-4 flex items-center gap-3 text-[11px] text-muted-foreground">
                  <span>Pressure</span>
                  <div className="flex h-3 w-32 overflow-hidden rounded-full">
                    {[0.06, 0.2, 0.35, 0.55, 0.75, 0.9, 1].map((alpha) => (
                      <div
                        key={alpha}
                        className="flex-1"
                        style={{ background: `hsl(var(--danger) / ${alpha})` }}
                      />
                    ))}
                  </div>
                  <span className="ml-auto inline-flex items-center gap-1">
                    <Bike className="h-3 w-3" /> Top {heatStores.length} stores by pressure
                  </span>
                </div>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      <div className="mt-4">
        {query.isError ? null : (
          <DataView<RosterCoverageStoreRow>
            title="By store"
            data={filtered}
            columns={columns}
            isLoading={query.isLoading}
            search={{ value: search, onChange: setSearch, placeholder: 'Search store…' }}
            emptyTitle="No stores in scope"
          />
        )}
      </div>
    </>
  );
}

function Cell({ cell, peak }: { cell: RosterCoverageCell | undefined; peak: number }) {
  const pressure = cell?.pressure ?? 0;
  const alpha = peak > 0 ? Math.max(0.04, Math.min(1, pressure / peak)) : 0;
  const empty = !cell || (cell.demand === 0 && cell.coverage === 0);
  return (
    <div
      title={
        cell
          ? `${cell.store_name} ${pad(cell.hour)}:00 — ${cell.demand} orders, ${cell.coverage} riders (${pressure.toFixed(2)} / rider)`
          : 'No data'
      }
      className={cn(
        'relative h-7 rounded-sm transition-colors',
        empty && 'bg-muted/30',
        cell && cell.demand > 0 && cell.coverage === 0 && 'ring-1 ring-danger',
      )}
      style={!empty ? { background: `hsl(var(--danger) / ${alpha})` } : undefined}
    />
  );
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}
